import { useEffect } from 'react';

import { useFetchData } from '@/api/apiHooks';
import { useReportContext } from '@/context/ReportContext';
import { PanelDropDownItem } from '@/utils/types';
import { URLS } from '@/utils/urls';

interface UseFetchBillingReportDataProps {
  url: (companyId, queryString) => string;
}

/**
 * Billing & Invoicing reports are company-level and unfiltered: the endpoints
 * only take a companyId (they ignore any query string), so unlike
 * useFetchReportData we gate the fetch solely on a selected company and never
 * require panel selection or a date range.
 */
export const useFetchBillingReportData = ({
  url,
}: UseFetchBillingReportDataProps) => {
  const { selectedCompany, setLastUpdatedAt } = useReportContext();
  const { isLoading, data, error } = useFetchData<any>({
    url: url(selectedCompany, ''),
    enabled: !!selectedCompany,
  });

  const { data: panelsData } = useFetchData<PanelDropDownItem[]>({
    url: URLS.getAllCompanyPanels(selectedCompany),
    enabled: !!selectedCompany,
    staleTime: 10 * 60 * 60 * 1000,
  });

  useEffect(() => {
    if (data) {
      setLastUpdatedAt(data?.last_updated_at);
    }
  }, [data, setLastUpdatedAt]);

  return {
    isLoading,
    data,
    error,
    panelsData,
  };
};
